import { useEffect, useMemo, useRef } from 'react';

import { faShieldCheck } from '@awesome.me/kit-f3dc60f0d0/icons/classic/solid';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button, Card } from '@repo/videa-components';
import { useAtomValue } from 'jotai';

import { useLatest } from '@videa/utils';

import { appointmentDataAtom } from '../../atoms/appointment';
import {
  hasAudioActivityAtom,
  recorderStatusAtom,
} from '../../atoms/recording';
import { useMicrophoneContext } from '../../contexts/MicrophoneContext';
import { reportMicLoadingStallToSentry } from '../../diagnostics/micLoadingStallTelemetry';
import { SheetFooter } from '../SheetFooter';

import { AudioFileUpload } from './AudioFileUpload';

interface ConsentFlowProps {
  monitoringStream: MediaStream | null;
  mediaBlobUrl?: string;
  buttonStyling: string;
  isSheetOpen: boolean;
  isTemplateSelected: boolean;
  isVoiceNotesConsentEnabled: boolean;
  isAudioUploadEnabled?: boolean;
  showDevTools?: boolean;
  onConsentDenied: () => void;
  onStartProviderOnlyRecording: () => void;
  onStartRecording: () => void;
  onAudioFileSelected?: (file: File, blobUrl: string) => void;
  compactMode?: boolean; // Stack buttons vertically for narrow UIs
}

export const ConsentFlow = ({
  monitoringStream,
  mediaBlobUrl,
  buttonStyling,
  isTemplateSelected,
  isSheetOpen,
  isVoiceNotesConsentEnabled,
  isAudioUploadEnabled = false,
  showDevTools = false,
  compactMode = false,
  onConsentDenied,
  onStartProviderOnlyRecording,
  onStartRecording,
  onAudioFileSelected,
}: ConsentFlowProps) => {
  const {
    canRecord,
    hasPermission: hasMicrophonePermission,
    permissionState,
    selectedDeviceId,
    selectedDevice,
  } = useMicrophoneContext();
  const recorderStatus = useAtomValue(recorderStatusAtom);
  const hasAudioActivity = useAtomValue(hasAudioActivityAtom);
  const appointmentData = useAtomValue(appointmentDataAtom);
  const loadingStartAtRef = useRef<number | null>(null);
  const hasReportedForCurrentLoadRef = useRef(false);

  // Refs for telemetry payload — read inside the 10s timeout so values are
  // fresh at report time without resetting the timer on every change.
  const monitoringStreamRef = useLatest(monitoringStream);
  const hasMicrophonePermissionRef = useLatest(hasMicrophonePermission);
  const permissionStateRef = useLatest(permissionState);
  const canRecordRef = useLatest(canRecord);
  const selectedDeviceIdRef = useLatest(selectedDeviceId);
  const selectedDeviceLabelRef = useLatest(selectedDevice?.label ?? null);
  const hasAudioActivityRef = useLatest(hasAudioActivity);
  const recorderStatusRef = useLatest(recorderStatus);
  const appointmentDataRef = useLatest(appointmentData);

  // Only show "Select Microphone First" if user hasn't granted permission.
  const showSelectMicrophoneFirst =
    !monitoringStream && !hasMicrophonePermission;

  // Show loading when we have permission but stream is temporarily null (during state transitions)
  const isStreamLoading = !monitoringStream && hasMicrophonePermission;

  useEffect(() => {
    // This creates a single "loading window": once loading resolves we reset
    // and allow a future stall to be reported again.
    if (!isStreamLoading) {
      loadingStartAtRef.current = null;
      hasReportedForCurrentLoadRef.current = false;
      return;
    }

    if (loadingStartAtRef.current === null) {
      loadingStartAtRef.current = Date.now();
    }

    if (hasReportedForCurrentLoadRef.current) {
      return;
    }

    const timeoutId = window.setTimeout(() => {
      if (hasReportedForCurrentLoadRef.current) {
        return;
      }

      // Report once per loading window to avoid noisy duplicate events.
      hasReportedForCurrentLoadRef.current = true;

      const loadingDurationMs =
        loadingStartAtRef.current !== null
          ? Date.now() - loadingStartAtRef.current
          : 10_000;

      const stream = monitoringStreamRef.current;
      const stallPayload = {
        loadingDurationMs,
        hasMicrophonePermission: hasMicrophonePermissionRef.current,
        permissionState: permissionStateRef.current,
        canRecord: canRecordRef.current,
        selectedDeviceId: selectedDeviceIdRef.current,
        selectedDeviceLabel: selectedDeviceLabelRef.current,
        hasAudioActivity: hasAudioActivityRef.current,
        isMonitoringStreamPresent: !!stream,
        isMonitoringStreamActive: !!stream?.active,
        recorderStatus: recorderStatusRef.current,
        appointmentData: appointmentDataRef.current,
      };

      reportMicLoadingStallToSentry(stallPayload);
      // eslint-disable-next-line react-hooks/exhaustive-deps -- useLatest refs are stable
    }, 10_000);

    return () => {
      window.clearTimeout(timeoutId);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isStreamLoading]);

  const shouldDisableButtons = useMemo(() => {
    return !isTemplateSelected || !monitoringStream || !canRecord;
  }, [isTemplateSelected, monitoringStream, canRecord]);

  const renderAudioUpload = () => {
    if (!isAudioUploadEnabled || !onAudioFileSelected || !showDevTools)
      return null;

    return (
      <div className="vai-mt-4 vai-pt-4 vai-border-t vai-border-gray-200 vai-w-full vai-flex vai-flex-col vai-items-center">
        <p className="vai-text-sm vai-text-gray-500 vai-mb-2">
          Or upload a pre-recorded audio file
        </p>
        <AudioFileUpload
          onFileSelected={onAudioFileSelected}
          isDisabled={!isTemplateSelected}
          size={isSheetOpen ? 'default' : 'sm'}
        />
      </div>
    );
  };

  const buttonSize = isSheetOpen ? 'default' : 'sm';
  const compactText = compactMode ? ' vai-text-xs' : '';

  const buttons = isVoiceNotesConsentEnabled ? (
    <div
      className={`vai-flex vai-flex-row ${
        compactMode ? 'vai-gap-x-1' : 'vai-gap-x-3'
      }`}
    >
      <Button
        onClick={onConsentDenied}
        variant="sheet"
        size={buttonSize}
        className={compactText}
        data-testid="consent-denied-button"
      >
        Consent Denied
      </Button>

      <Button
        onClick={onStartProviderOnlyRecording}
        variant={'sheet'}
        size={buttonSize}
        className={compactText}
        disabled={shouldDisableButtons}
        data-testid="provider-only-recording-button"
      >
        Provider Only Recording
      </Button>

      <Button
        onClick={onStartRecording}
        variant={mediaBlobUrl ? 'sheet' : 'default'}
        className={`${buttonStyling} hover:vai-ring-primary/50${compactText}`}
        size={buttonSize}
        disabled={shouldDisableButtons}
        loading={isStreamLoading}
        data-testid="start-recording-button-consent-enabled"
      >
        {showSelectMicrophoneFirst
          ? 'Select Microphone First'
          : 'Yes, Start Recording'}
      </Button>
    </div>
  ) : (
    <Button
      onClick={onStartRecording}
      variant={mediaBlobUrl ? 'sheet' : 'default'}
      className={`${buttonStyling} hover:vai-ring-primary/50`}
      size={isSheetOpen ? 'default' : 'sm'}
      disabled={shouldDisableButtons}
      loading={isStreamLoading}
      data-testid="start-recording-button"
    >
      {showSelectMicrophoneFirst
        ? 'Select Microphone First'
        : 'Start Recording'}
    </Button>
  );

  return (
    <div className="vai-flex vai-flex-col vai-items-center vai-w-full vai-flex-1 vai-min-h-0">
      {/* Scrollable main content */}
      <div className="vai-flex vai-flex-col vai-items-center vai-w-full vai-flex-1 vai-min-h-0 vai-overflow-auto">
        {renderAudioUpload()}
      </div>

      {/* Sticky footer: consent block + action buttons */}
      <SheetFooter className="vai-flex-col vai-items-center vai-gap-6">
        {isVoiceNotesConsentEnabled && (
          <Card
            className="!vai-bg-amber-50 !vai-shadow-none vai-border vai-border-solid vai-border-amber-100 vai-p-2"
            data-testid="recording-consent-banner"
          >
            <div className="vai-flex vai-items-center vai-gap-3">
              <div className="vai-flex vai-items-center vai-justify-center vai-w-4 vai-h-4 vai-flex-shrink-0 vai-text-amber-600">
                <FontAwesomeIcon icon={faShieldCheck} />
              </div>
              <span className="vai-text-sm vai-text-amber-700">
                Obtain recording consent: &quot;
                <span className="vai-font-medium">
                  Can I record this visit so I can focus fully on you?
                </span>
                &quot;
              </span>
            </div>
          </Card>
        )}
        {buttons}
      </SheetFooter>
    </div>
  );
};
